import { API_BASE_URL } from './config.js';
import fetchWrapper from '../components/utility/fetchWrapper.js'
import { parseApiErrorMessage } from '../utils/parseApiErrorMessage.js';

export const databricksService = {
  // Get all Databricks accounts
  getAccounts: async () => {
    try {
      const response = await fetchWrapper(`${API_BASE_URL}/api/v1/databricks/accounts`);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch Databricks accounts: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.accounts) {
        return [];
      }
      
      if (!Array.isArray(data.accounts)) {
        return Array.isArray(data) ? data : [data];
      }
      
      return data.accounts;
    } catch (error) {
      console.error('Error fetching Databricks accounts:', error);
      throw error;
    }
  },

  // Get account details - accounts are already returned from getAccounts
  getAccountDetails: async (account_id) => {
    try {
      // Since the backend returns all account info in getAccounts, we can use that
      // This method is kept for backward compatibility but may not be needed
      const response = await fetchWrapper(`${API_BASE_URL}/api/v1/databricks/accounts`);
      if (!response.ok) throw new Error('Failed to fetch Databricks accounts');
      const data = await response.json();
      const accounts = data.accounts || [];
      const account = accounts.find(acc => acc.account_id === account_id);
      if (!account) {
        throw new Error('Account not found');
      }
      return account;
    } catch (error) {
      console.error('Error fetching account details:', error);
      throw error;
    }
  },

  // Get workspaces for an account
  getWorkspaces: async (account_id) => {
    try {
      const response = await fetchWrapper(
        `${API_BASE_URL}/api/v1/databricks/workspaces?account_id=${account_id}`
      );
      if (!response.ok) throw new Error('Failed to fetch workspaces');
      const data = await response.json();
      return data.workspaces || [];
    } catch (error) {
      console.error('Error fetching workspaces:', error);
      throw error;
    }
  },

  // Get audit log path
  getAuditLogPath: async (account_id, client_id, client_secret, account_url) => {
    try {
      const response = await fetchWrapper(
        `${API_BASE_URL}/api/v1/databricks/audit/log-path?account_id=${account_id}&client_id=${client_id}&client_secret=${client_secret}&account_url=${encodeURIComponent(account_url)}`
      );
      if (!response.ok) throw new Error('Failed to fetch audit log path');
      return await response.json();
    } catch (error) {
      console.error('Error fetching audit log path:', error);
      throw error;
    }
  },

  // Get workspace credentials
  getWorkspaceCredentials: async (workspace_id) => {
    try {
      const response = await fetchWrapper(`${API_BASE_URL}/api/v1/databricks/secrets/workspace/${workspace_id}`);
      if (!response.ok) throw new Error('Failed to fetch workspace credentials');
      return await response.json();
    } catch (error) {
      console.error('Error fetching workspace credentials:', error);
      throw error;
    }
  },

  // Get cluster log paths
  getClusterLogPaths: async (workspace_url, pat_token) => {
    try {
      const response = await fetchWrapper(
        `${API_BASE_URL}/api/v1/databricks/clusters/paths?workspace_url=${encodeURIComponent(workspace_url)}&pat_token=${encodeURIComponent(pat_token)}`
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch cluster log paths: ${errorText || response.statusText}`);
      }
      const data = await response.json();
      // API returns { cluster_paths: [], message: "Success" }
      return { cluster_log_paths: data.cluster_paths || [] };
    } catch (error) {
      console.error('Error fetching cluster log paths:', error);
      throw error;
    }
  },

  // Onboard audit logs
  onboardAuditLogs: async (payload) => {
    try {
      const response = await fetchWrapper(`${API_BASE_URL}/api/v1/databricks/audit/onboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Failed to onboard audit logs');
      return await response.json();
    } catch (error) {
      console.error('Error onboarding audit logs:', error);
      throw error;
    }
  },

  // Delete Databricks account
  deleteAccount: async (accountId) => {
    try {
      const response = await fetchWrapper(
        `${API_BASE_URL}/api/v1/databricks/accounts/${encodeURIComponent(accountId)}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to delete Databricks account');
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting Databricks account:', error);
      throw error;
    }
  },

  // Onboard Databricks account
  onboardAccount: async (payload) => {
    try {
      const response = await fetchWrapper(`${API_BASE_URL}/api/v1/databricks/onboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Failed to onboard Databricks account');
      return await response.json();
    } catch (error) {
      console.error('Error onboarding Databricks account:', error);
      throw error;
    }
  },

  // Onboard Databricks logs configuration
  onboardLogs: async (payload) => {
    try {
      const response = await fetchWrapper(`${API_BASE_URL}/databricks/onboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(parseApiErrorMessage(errorText, 'Failed to onboard logs'));
      }
      return await response.json();
    } catch (error) {
      console.error('Error onboarding logs:', error);
      throw error;
    }
  },

  // Deboard Databricks logs configuration
  deboardLogs: async (workspaceName) => {
    try {
      const response = await fetchWrapper(`${API_BASE_URL}/databricks/deboard`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ workspace_name: workspaceName }),
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to deboard logs: ${errorText || response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error deboarding logs:', error);
      throw error;
    }
  },

  // Get Databricks metrics overview
  getMetricsOverview: async (workspaceUrl, token) => {
    try {
      const response = await fetchWrapper(
        `${API_BASE_URL}/api/v1/databricks/metrics/overview?workspace_url=${encodeURIComponent(workspaceUrl)}&token=${encodeURIComponent(token)}`
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch metrics overview: ${errorText || response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching metrics overview:', error);
      throw error;
    }
  },

  // Get Databricks metrics details by category
  getMetricsDetails: async (workspaceUrl, token, category, filterName = null) => {
    try {
      let url = `${API_BASE_URL}/api/v1/databricks/metrics/details?workspace_url=${encodeURIComponent(workspaceUrl)}&token=${encodeURIComponent(token)}&category=${encodeURIComponent(category)}`;
      if (filterName) {
        url += `&filter_name=${encodeURIComponent(filterName)}`;
      }
      const response = await fetchWrapper(url);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch metrics details: ${errorText || response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching metrics details:', error);
      throw error;
    }
  },

  // Get workspaces for metrics onboarding
  getMetricsWorkspaces: async (account_id) => {
    try {
      const response = await fetchWrapper(
        `${API_BASE_URL}/api/v1/databricks/metrics/workspaces?account_id=${account_id}`
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch workspaces: ${errorText || response.statusText}`);
      }
      const data = await response.json();
      return data.workspaces || [];
    } catch (error) {
      console.error('Error fetching metrics workspaces:', error);
      throw error;
    }
  },

  // Get clusters for metrics onboarding
  getMetricsClusters: async (workspace_id, deployment_name, account_id = null, account_name = null, pat_token = null) => {
    try {
      let url = `${API_BASE_URL}/api/v1/databricks/metrics/clusters?workspace_id=${encodeURIComponent(workspace_id)}&deployment_name=${encodeURIComponent(deployment_name)}`;
      if (account_name) {
        url += `&account_name=${encodeURIComponent(account_name)}`;
      } else if (account_id) {
        url += `&account_id=${encodeURIComponent(account_id)}`;
      } else if (pat_token) {
        url += `&pat_token=${encodeURIComponent(pat_token)}`;
      } else {
        throw new Error('Either account_id/account_name or pat_token must be provided');
      }
      
      const response = await fetchWrapper(url);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch clusters: ${errorText || response.statusText}`);
      }
      const data = await response.json();
      // Return full response to allow access to instance_profiles_from_clusters
      return data;
    } catch (error) {
      console.error('Error fetching metrics clusters:', error);
      throw error;
    }
  },

  // Get metrics status
  getMetricsStatus: async (workspace_id, deployment_name, cluster_id, pat_token) => {
    try {
      const response = await fetchWrapper(
        `${API_BASE_URL}/api/v1/databricks/metrics/status?workspace_id=${encodeURIComponent(workspace_id)}&deployment_name=${encodeURIComponent(deployment_name)}&cluster_id=${encodeURIComponent(cluster_id)}&pat_token=${encodeURIComponent(pat_token)}`
      );
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch metrics status: ${errorText || response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching metrics status:', error);
      throw error;
    }
  },

  // Toggle metrics (enable/disable)
  toggleMetrics: async (workspace_id, deployment_name, cluster_id, enable, account_id = null, account_name = null, pat_token = null, instance_profile_arn = null) => {
    try {
      // Normalize and validate deployment_name before making the request
      const normalizedDeploymentName = deployment_name && typeof deployment_name === 'string' 
        ? deployment_name.trim() 
        : (deployment_name || null);
      
      console.log('[toggleMetrics] Validating deployment_name:', {
        original: deployment_name,
        normalized: normalizedDeploymentName,
        type: typeof deployment_name,
        workspace_id,
        cluster_id,
        enable
      });
      
      if (!normalizedDeploymentName || 
          normalizedDeploymentName === 'undefined' || 
          normalizedDeploymentName === 'null' || 
          normalizedDeploymentName === null || 
          normalizedDeploymentName === '') {
        const errorMsg = `deployment_name is required and must be a valid Databricks workspace deployment name. Received: ${JSON.stringify(deployment_name)}. Please refresh the page and try again.`;
        console.error('[toggleMetrics] Validation failed:', errorMsg);
        throw new Error(errorMsg);
      }

      let url = `${API_BASE_URL}/api/v1/databricks/metrics/toggle?workspace_id=${encodeURIComponent(workspace_id)}&deployment_name=${encodeURIComponent(normalizedDeploymentName)}&cluster_id=${encodeURIComponent(cluster_id)}&enable=${enable}`;
      
      // Add account_name or account_id
      if (account_name) {
        url += `&account_name=${encodeURIComponent(account_name)}`;
      } else if (account_id) {
        url += `&account_id=${encodeURIComponent(account_id)}`;
      }
      
      // Add pat_token only if provided (otherwise backend will use OAuth from account_id/account_name)
      if (pat_token) {
        url += `&pat_token=${encodeURIComponent(pat_token)}`;
      }
      
      // Add instance_profile_arn if provided (required when enabling)
      if (instance_profile_arn) {
        url += `&instance_profile_arn=${encodeURIComponent(instance_profile_arn)}`;
      }
      
      const response = await fetchWrapper(url, {
        method: 'POST',
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to toggle metrics: ${errorText || response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error toggling metrics:', error);
      throw error;
    }
  },

  // Get instance profiles for a workspace
  getInstanceProfiles: async (deployment_name, account_id = null, account_name = null, pat_token = null) => {
    try {
      let url = `${API_BASE_URL}/api/v1/databricks/metrics/instance-profiles?deployment_name=${encodeURIComponent(deployment_name)}`;
      if (account_name) {
        url += `&account_name=${encodeURIComponent(account_name)}`;
      } else if (account_id) {
        url += `&account_id=${encodeURIComponent(account_id)}`;
      } else if (pat_token) {
        url += `&pat_token=${encodeURIComponent(pat_token)}`;
      } else {
        throw new Error('Either account_id/account_name or pat_token must be provided');
      }
      
      const response = await fetchWrapper(url);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch instance profiles: ${errorText || response.statusText}`);
      }
      const data = await response.json();
      return data.instance_profiles || [];
    } catch (error) {
      console.error('Error fetching instance profiles:', error);
      throw error;
    }
  },

  // Get monitored metrics from backend
  getMonitoredMetrics: async (accountId = null) => {
    try {
      let url = `${API_BASE_URL}/api/v1/databricks/metrics/monitored`;
      if (accountId) {
        url += `?account_id=${encodeURIComponent(accountId)}`;
      }
      
      const response = await fetchWrapper(url);
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch monitored metrics: ${errorText || response.statusText}`);
      }
      const data = await response.json();
      // Return the monitored_clusters array from the API response
      return data.monitored_clusters || data.metrics || data || [];
    } catch (error) {
      console.error('Error fetching monitored metrics:', error);
      throw error;
    }
  },

  // Local storage operations for Databricks accounts
  saveToLocalStorage: (accountData) => {
    try {
      const existingAccounts = databricksService.getFromLocalStorage();
      const accountKey = `${accountData.account_id}`;
      existingAccounts[accountKey] = {
        account_name: accountData.account_name || `Databricks Account ${accountData.account_id}`,
        account_id: accountData.account_id,
        account_url: accountData.account_url,
        client_id: accountData.client_id,
        client_secret: accountData.client_secret,
        pat_token: accountData.pat_token,
        created_at: new Date().toISOString(),
      };
      localStorage.setItem('databricks_accounts', JSON.stringify(existingAccounts));
      return true;
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      return false;
    }
  },

  getFromLocalStorage: () => {
    try {
      const data = localStorage.getItem('databricks_accounts');
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return {};
    }
  },

  deleteFromLocalStorage: (accountId) => {
    try {
      const existingAccounts = databricksService.getFromLocalStorage();
      delete existingAccounts[accountId];
      localStorage.setItem('databricks_accounts', JSON.stringify(existingAccounts));
      return true;
    } catch (error) {
      console.error('Error deleting from localStorage:', error);
      return false;
    }
  },
};
